<template>
  <div class="container py-4">
    <div class="page-card">
      <h4 class="mb-1 section-title-accent">System settings</h4>
      <p class="small text-muted mb-4">
        Operating currency applies to POS, receipts, and reports.
      </p>

      <b-form @submit.prevent="save">
        <b-form-group label="Operating currency">
          <b-form-select
            v-model="form.currencyCode"
            class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
            :options="currencyOptions"
          />
          <b-form-text>
            Legacy POS_Test1 stores this on the business record; AKKURATE POS
            uses system-wide settings.
          </b-form-text>
        </b-form-group>

        <b-form-group label="Tax display">
          <div class="setting-toggle-row">
            <div class="setting-toggle-info">
              <div class="setting-toggle-title">
                {{
                  form.displayTax
                    ? "Tax shown on receipts"
                    : "Tax hidden on receipts"
                }}
              </div>
              <div class="setting-toggle-desc">
                When enabled, VAT / levy breakdowns are shown to customers on
                receipts and the POS screen.
              </div>
            </div>

            <label class="modern-switch">
              <input type="checkbox" v-model="form.displayTax" />
              <span class="modern-switch-track">
                <span class="modern-switch-thumb"></span>
              </span>
            </label>
          </div>
        </b-form-group>

        <b-form-group label="Cash tender presets (POS)">
          <b-form-input
            class="form-control"
            v-model="tenderPresetsText"
            placeholder="5, 10, 20, 50, 100"
          />
          <b-form-text
            >Comma-separated note amounts for quick cash buttons.</b-form-text
          >
        </b-form-group>

        <div class="d-flex align-items-center gap-3 mt-3">
          <icon-button
            :func="() => save()"
            :icon="'save'"
            :title="'Save settings'"
            :classes="'akkurate-green-btn btn-size2'"
            :btnName="'Save settings'"
            :disabled="saving"
          />
          <span class="text-muted small"
            >Preview: {{ formatMoney(1234.56) }}</span
          >
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import { CURRENCY_OPTIONS } from "@/core/helpers/money";
import IconButton from "@/components/IconButton.vue";

export default {
  name: "Settings",
  components: {
    IconButton,
  },
  data() {
    return {
      saving: false,
      form: { currencyCode: "GHS", displayTax: true },
      tenderPresetsText: "5, 10, 20, 50, 100",
      currencyOptions: CURRENCY_OPTIONS,
    };
  },
  computed: {
    ...mapGetters(["currencyCode"]),
  },
  async mounted() {
    const settings = await this.$store.dispatch("loadSettings");
    this.form.currencyCode = this.currencyCode || "GHS";
    this.form.displayTax = !!(settings?.displayTax ?? true);
    if (settings?.tenderPresets?.length) {
      this.tenderPresetsText = settings.tenderPresets.join(", ");
    }
  },
  methods: {
    parseTenderPresets() {
      return this.tenderPresetsText
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n) && n > 0);
    },
    async save() {
      this.saving = true;
      try {
        const result = await ApiService.put("settings", {
          currencyCode: this.form.currencyCode,
          displayTax: this.form.displayTax,
          tenderPresets: this.parseTenderPresets(),
        });
        this.$store.commit("SET_CURRENCY", result);
        toast.fire({
          title: "Settings saved",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not save settings",
          icon: "danger",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style scoped>
.setting-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.setting-toggle-row:hover {
  border-color: #d1d5db;
  background: #f5f5f5;
}

.setting-toggle-title {
  font-weight: 600;
  font-size: 0.925rem;
  color: #1f2937;
  margin-bottom: 2px;
}

.setting-toggle-desc {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.4;
  max-width: 420px;
}

/* --- modern switch --- */
.modern-switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
  flex-shrink: 0;
  cursor: pointer;
}

.modern-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.modern-switch-track {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 999px;
  transition: background 0.2s ease;
}

.modern-switch-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
}

.modern-switch input:checked + .modern-switch-track {
  background: #16a34a; /* matches your akkurate-green-btn tone; swap if needed */
}

.modern-switch input:checked + .modern-switch-track .modern-switch-thumb {
  transform: translateX(20px);
}

.modern-switch input:focus-visible + .modern-switch-track {
  outline: 2px solid #16a34a;
  outline-offset: 2px;
}
</style>
